import { useFormikContext } from "formik";
import { useWorkspaceAccounts } from "@api/account.api";
import { SCHEDULE_POLICY_KIND_MODES } from "@common/helpers/domains/accounting/schedulePolicyKind";
import { Button } from "@design-system/Button/Button";
import { AccountsIcon, FolderIcon, PlusIcon } from "@icons";
import { ScheduleAccountBlock } from "./ScheduleAccountBlock";
import { ScheduleDetailsCard } from "./ScheduleDetailsCard";
import { ScheduleFormSection } from "./ScheduleFormSection";
import { ScheduleRecognitionSection } from "./ScheduleRecognitionSection";
import {
    makeScheduleAccountBlock,
    ScheduleAccountBlockForm,
    ScheduleTemplateFormValues,
} from "./scheduleFormHelpers";
import styles from "./ScheduleDrawer.module.scss";

export const ScheduleForm: React.FC = () => {
    const { values, errors, submitCount, setFieldValue } =
        useFormikContext<ScheduleTemplateFormValues>();
    const workspaceAccounts = useWorkspaceAccounts();
    const shouldShowErrors = submitCount > 0;
    const accountBlocksErrors =
        typeof errors.accountBlocks === "string"
            ? undefined
            : errors.accountBlocks;
    const blocksArrayError =
        shouldShowErrors && typeof errors.accountBlocks === "string"
            ? errors.accountBlocks
            : null;

    const updateBlock = (
        index: number,
        patch: Partial<ScheduleAccountBlockForm>,
    ) => {
        setFieldValue(
            "accountBlocks",
            values.accountBlocks.map((block, blockIndex) =>
                blockIndex === index ? { ...block, ...patch } : block,
            ),
        );
    };

    return (
        <div className={styles.formPane}>
            <ScheduleFormSection
                title="Policy details"
                icon={<FolderIcon className="icon-color-gray-600" />}
                iconClassName={styles.sectionIconDetails}
            >
                <ScheduleDetailsCard />
            </ScheduleFormSection>

            <ScheduleFormSection
                title="Accounts"
                icon={<AccountsIcon className="icon-color-gray-700" />}
                iconClassName={styles.sectionIconAccounts}
                sectionClassName={styles.sectionToRecognition}
            >
                <div className={styles.card}>
                    {values.accountBlocks.map((block, index) => (
                        <ScheduleAccountBlock
                            key={block.key}
                            block={block}
                            mode={SCHEDULE_POLICY_KIND_MODES[values.kind]}
                            accounts={workspaceAccounts.data ?? []}
                            isAccountsLoading={workspaceAccounts.isLoading}
                            canRemove={values.accountBlocks.length > 1}
                            blockErrors={accountBlocksErrors?.[index]}
                            shouldShowErrors={shouldShowErrors}
                            onChange={(patch) => updateBlock(index, patch)}
                            onRemove={() =>
                                setFieldValue(
                                    "accountBlocks",
                                    values.accountBlocks.filter(
                                        (_, blockIndex) => blockIndex !== index,
                                    ),
                                )
                            }
                        />
                    ))}
                    {blocksArrayError && (
                        <div className={styles.cardFooterRow}>
                            <p className={styles.fieldError}>
                                {blocksArrayError}
                            </p>
                        </div>
                    )}
                    <div className={styles.cardFooterRow}>
                        <Button
                            size="md"
                            type="button"
                            variant="primary"
                            onClick={() =>
                                setFieldValue("accountBlocks", [
                                    ...values.accountBlocks,
                                    makeScheduleAccountBlock(),
                                ])
                            }
                            leftIcon={<PlusIcon />}
                        >
                            Add
                        </Button>
                    </div>
                </div>
            </ScheduleFormSection>

            <ScheduleRecognitionSection />
        </div>
    );
};
